/**
 * Dev Menu Configuration
 *
 * Controls visibility of developer tools section in the navigation sidebar.
 * - Automatically visible in DEV mode (import.meta.env.DEV)
 * - Can be toggled via console: __showDevMenu() / __hideDevMenu()
 * - Toggle state persists in localStorage
 */

const STORAGE_KEY = 'dev_menu_visible';

type DevMenuListener = (visible: boolean) => void;
const listeners = new Set<DevMenuListener>();

function getStoredVisibility(): boolean | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function setStoredVisibility(visible: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(visible));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Check if dev menu should be visible
 * Priority: localStorage override > DEV mode default
 */
export function isDevMenuVisible(): boolean {
  const stored = getStoredVisibility();
  if (stored !== null) return stored;
  return import.meta.env.DEV;
}

/**
 * Show the dev menu (persists across refreshes)
 */
export function showDevMenu(): void {
  setStoredVisibility(true);
  notifyListeners();
  console.log('🔧 Dev menu enabled');
}

/**
 * Hide the dev menu (persists across refreshes)
 */
export function hideDevMenu(): void {
  setStoredVisibility(false);
  notifyListeners();
  console.log('🔧 Dev menu disabled');
}

/**
 * Toggle dev menu visibility
 */
export function toggleDevMenu(): boolean {
  const newState = !isDevMenuVisible();
  setStoredVisibility(newState);
  notifyListeners();
  console.log(`🔧 Dev menu ${newState ? 'enabled' : 'disabled'}`);
  return newState;
}

/**
 * Reset to default behavior (DEV mode = visible, PROD = hidden)
 */
export function resetDevMenu(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
  notifyListeners();
  console.log('🔧 Dev menu reset to default');
}

function notifyListeners(): void {
  const visible = isDevMenuVisible();
  listeners.forEach((listener) => listener(visible));
}

/**
 * Subscribe to visibility changes
 */
export function subscribeDevMenu(listener: DevMenuListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Expose toggle functions on window for console access
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__showDevMenu = showDevMenu;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__hideDevMenu = hideDevMenu;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__toggleDevMenu = toggleDevMenu;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__resetDevMenu = resetDevMenu;
